import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import marketingData from "./MarketingData";
import styles from "./DigitalMarketing.module.css";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Helper: stable Fisher-Yates shuffle
 */
const shuffle = (arr) => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const getRandomQuestions = (data, count = 12) => {
  const easyQuestions = data.filter((q) => q.level === "easy");
  const mediumQuestions = data.filter((q) => q.level === "medium");
  const hardQuestions = data.filter((q) => q.level === "hard");

  const easyCount = Math.min(4, easyQuestions.length);
  const remainingCount = Math.min(count, data.length) - easyCount;
  const mediumCount = Math.min(
    Math.floor(remainingCount / 2),
    mediumQuestions.length
  );
  const hardCount = Math.min(
    remainingCount - mediumCount,
    hardQuestions.length
  );

  const selectedEasy = shuffle(easyQuestions).slice(0, easyCount);
  const selectedMedium = shuffle(mediumQuestions).slice(0, mediumCount);
  const selectedHard = shuffle(hardQuestions).slice(0, hardCount);

  let allSelected = shuffle([
    ...selectedEasy,
    ...selectedMedium,
    ...selectedHard,
  ]);

  if (allSelected.length < count) {
    const remainingQuestions = shuffle([
      ...easyQuestions,
      ...mediumQuestions,
      ...hardQuestions,
    ]).filter((q) => !allSelected.includes(q));
    const additional = remainingQuestions.slice(0, count - allSelected.length);
    allSelected = [...allSelected, ...additional];
    allSelected = shuffle(allSelected);
  }

  return allSelected;
};

const allQuizData = marketingData || [];

const DigitalMarketing = () => {
  const navigate = useNavigate();

  // quiz state
  const [quizData, setQuizData] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({}); // { [index]: option }
  const answersRef = useRef(answers);
  const [score, setScore] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [showDisqualified, setShowDisqualified] = useState(false);
  const [error, setError] = useState("");
  const [direction, setDirection] = useState("forward");
  const [timeLeft, setTimeLeft] = useState(10);
  const [showRules, setShowRules] = useState(true);

  const timerRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // Load questions once after rules closed
  useEffect(() => {
    if (!showRules && quizData.length === 0) {
      const loaded = getRandomQuestions(allQuizData, 12);
      setQuizData(loaded);
      setCurrentQuestion(0);
      setAnswers({});
      answersRef.current = {};
      setShowResults(false);
      setScore(null);
      setShowDisqualified(false);
      setError("");
      setTimeLeft(10);
    }
    // only want to run when showRules changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRules]);

  const totalQuestions = quizData.length;

  // Timer effect (runs when currentQuestion or quizData changes)
  useEffect(() => {
    // clear existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (
      quizData.length === 0 ||
      currentQuestion >= totalQuestions ||
      showResults ||
      showDisqualified
    ) {
      return;
    }

    setTimeLeft(10);

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        const next = prev - 1;

        if (next <= 0) {
          // time's up for this question
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }

          const hasAnswered = Object.prototype.hasOwnProperty.call(
            answersRef.current,
            currentQuestion
          );

          if (!hasAnswered) {
            if (isMounted.current) setShowDisqualified(true);
            return 0;
          } else {
            if (currentQuestion < totalQuestions - 1) {
              if (isMounted.current) {
                setCurrentQuestion((q) => q + 1);
                setDirection("forward");
              }
            } else {
              if (isMounted.current) handleSubmit();
            }
            return 0;
          }
        }

        return next;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, quizData, showResults, showDisqualified]);

  // keep answersRef up-to-date to avoid stale closures
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  const questionVariants = {
    hidden: (dir) => ({ opacity: 0, x: dir === "forward" ? 100 : -100 }),
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3, ease: "easeInOut" },
    },
    exit: (dir) => ({
      opacity: 0,
      x: dir === "forward" ? -100 : 100,
      transition: { duration: 0.3, ease: "easeInOut" },
    }),
  };

  const errorShake = {
    shake: { x: [0, -8, 8, -8, 8, 0], transition: { duration: 0.4 } },
  };

  const handleOptionSelect = (option) => {
    if (timeLeft <= 0 || showDisqualified || showResults) return;

    setAnswers((prev) => {
      const next = { ...prev, [currentQuestion]: option };
      answersRef.current = next;
      return next;
    });
    setError("");
  };

  const handleNext = () => {
    if (answers[currentQuestion] === undefined) {
      setError("Please select an answer before proceeding.");
      return;
    }
    setError("");
    setDirection("forward");
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion((q) => q + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    // compute score separately
    let s = 0;
    for (let i = 0; i < totalQuestions; i++) {
      if (answers[i] !== undefined && answers[i] === quizData[i].answer) s++;
    }
    setScore(s);
    setShowResults(true);

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleRestart = () => {
    // cleanup timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    navigate("/");
  };

  const handleStartQuiz = () => {
    setShowRules(false);
  };

  // RULES screen
  if (showRules) {
    return (
      <div className={styles.quizContainer}>
        <div className={styles.rulesModal}>
          <h2 className={styles.rulesTitle}>Quiz Rules</h2>
          <div className={styles.rulesContent}>
            <ul className={styles.rulesList}>
              <li>
                1️⃣ You have <b>10 seconds</b> per question.
              </li>
              <li>2️⃣ You can change answers before time ends.</li>
              <li>3️⃣ All questions are mandatory.</li>
              <li>
                4️⃣ Fail to answer ➜ <b>Disqualified ❌</b>
              </li>
              <li>5️⃣ Score 0–8 = No Prize.</li>
            </ul>
          </div>
          <button onClick={handleStartQuiz} className={styles.startButton}>
            Start Now
          </button>
        </div>
      </div>
    );
  }

  // Disqualified screen
  if (showDisqualified) {
    return (
      <div className={styles.quizContainer}>
        <motion.div
          className={styles.quizCard}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className={styles.resultsTitle}>❌ Disqualified!</h2>
          <p className={styles.congratsText}>
            You didn’t answer in time. You are disqualified from this quiz.
          </p>
          <button
            onClick={handleRestart}
            className={styles.submitButton}
            style={{ width: "100%", marginTop: "2rem" }}
          >
            Go Home 🏠
          </button>
        </motion.div>
      </div>
    );
  }

  // Loading state
  if (quizData.length === 0) {
    return (
      <div className={styles.quizContainer}>
        <div className={styles.quizCard}>
          <div className={styles.loadingSpinner}>Loading quiz...</div>
        </div>
      </div>
    );
  }

  const progressPercent =
    totalQuestions > 0 ? ((currentQuestion + 1) / totalQuestions) * 100 : 0;

  return (
    <div className={styles.quizContainer}>
      <motion.div
        className={styles.quizCard}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <AnimatePresence mode="wait">
          {showResults ? (
            <motion.div
              key="results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={styles.resultsContainer}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className={styles.congratsIcon}
              >
                🎉
              </motion.div>
              <h2 className={styles.resultsTitle}>Quiz Results</h2>
              <p className={styles.congratsText}>You Completed the Quiz!</p>

              <div className={styles.codeDisplay}>
                <p className={styles.codeLabel}>Your Score:</p>
                <p className={styles.codeValue}>
                  {score}/{quizData.length}
                </p>
                <p className={styles.scoreMessage}>
                  {score <= 8 ? "Fair!" : score <= 10 ? "Good!" : "Superb!"}
                </p>
              </div>

              <button
                onClick={handleRestart}
                className={styles.submitButton}
                style={{ width: "100%", marginTop: "2rem" }}
              >
                Try Again
              </button>
            </motion.div>
          ) : (
            <motion.div key={`quiz-${currentQuestion}`}>
              <div className={styles.progressBarContainer}>
                <motion.div
                  className={styles.progressBar}
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </div>

              <div className={styles.questionHeader}>
                <div className={styles.questionProgress}>
                  Question {currentQuestion + 1} of {totalQuestions}
                </div>
                <div className={styles.timer}>
                  Time left: <strong>{timeLeft}s</strong>
                </div>
              </div>

              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentQuestion}
                  custom={direction}
                  variants={questionVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className={styles.questionWrapper}
                >
                  <h3 className={styles.questionText}>
                    {quizData[currentQuestion].question}
                  </h3>
                  <div className={styles.optionsContainer}>
                    {quizData[currentQuestion].options.map((option) => (
                      <button
                        key={`${currentQuestion}-${option}`}
                        className={`${styles.optionButton} ${
                          answers[currentQuestion] === option
                            ? styles.selectedOption
                            : ""
                        }`}
                        onClick={() => handleOptionSelect(option)}
                        disabled={timeLeft === 0}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>

              <AnimatePresence>
                {error && (
                  <motion.p
                    className={styles.errorText}
                    variants={errorShake}
                    animate="shake"
                    key="error"
                  >
                    {error}
                  </motion.p>
                )}
              </AnimatePresence>

              <div className={styles.navigationButtons}>
                {currentQuestion < totalQuestions - 1 ? (
                  <button onClick={handleNext} className={styles.submitButton}>
                    Next
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      if (answers[currentQuestion] === undefined) {
                        setError("Please select an answer before submitting.");
                        return;
                      }
                      handleSubmit();
                    }}
                    className={styles.submitButton}
                  >
                    Submit Quiz
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default DigitalMarketing;
